import { findAll, slugify } from './dom.js';

const CAPABILITY_SELECTOR = '[data-bp-product-filter="v2"]';
const READY_ATTRIBUTE = 'data-bp-filter-ready';
const OPEN_STATE = 'open';
const FOCUSABLE = 'button, input, [href], [tabindex]:not([tabindex="-1"])';
const PAGE_SIZE = 4;
const PHONE_QUERY = '(max-width: 767px)';
const LESS_THAN = /^(less than|under|below|<)\s*/iu;
const LEADING_NUMBER = /^(\d+(?:\.\d+)?)/u;
const collator = new Intl.Collator('en', { numeric: true, sensitivity: 'base' });

export function parseFacetList(text) {
  const values = [];
  for (const part of String(text ?? '').split(',')) {
    const value = part.trim().replace(/\s+/gu, ' ');
    if (value && !values.includes(value)) values.push(value);
  }
  return values;
}

export const slugifyValue = slugify;

// Range labels ("Less than 0.5 oz", "0.5 to 0.9 oz", "1 oz") sort by their first number, a "less than" bound just
// ahead of that number; every other pair falls back to a numeric-aware alphabetical compare.
function rangeKey(value) {
  const bounded = LESS_THAN.test(value);
  const match = value.replace(LESS_THAN, '').match(LEADING_NUMBER);
  if (!match) return null;
  return Number(match[1]) - (bounded ? 1e-6 : 0);
}

export function compareFacetValues(a, b) {
  const keyA = rangeKey(a);
  const keyB = rangeKey(b);
  if (keyA !== null && keyB !== null && keyA !== keyB) return keyA - keyB;
  return collator.compare(a, b);
}

export function collectOptions(products, groups) {
  const options = {};
  for (const group of groups) {
    const values = new Set();
    for (const product of products) for (const value of product[group] ?? []) values.add(value);
    options[group] = [...values].sort(compareFacetValues);
  }
  return options;
}

// OR within a group, AND across groups; a group with nothing selected does not filter.
export function matchProducts(products, selection) {
  const active = Object.entries(selection).filter(([, values]) => values.length > 0);
  return products.map((product) => active.every(([group, values]) => (
    (product[group] ?? []).some((value) => values.includes(value))
  )));
}

// Phones page the matches: only the first `limit` matching products show; the rest wait for Load More.
export function limitMatches(matches, limit) {
  let seen = 0;
  return matches.map((match) => match && (seen += 1) <= limit);
}

export function selectionToQuery(selection, groups, baseSearch = '') {
  const params = new URLSearchParams(baseSearch);
  for (const group of groups) {
    const values = selection[group] ?? [];
    if (values.length) params.set(group, values.map(slugifyValue).join(','));
    else params.delete(group);
  }
  const query = params.toString().replace(/%2C/gu, ',');
  return query ? `?${query}` : '';
}

export function queryToSelection(search, options) {
  const params = new URLSearchParams(search);
  const selection = {};
  for (const [group, values] of Object.entries(options)) {
    const wanted = (params.get(group) ?? '').split(',').map(slugifyValue).filter(Boolean);
    selection[group] = values.filter((value) => wanted.includes(slugifyValue(value)));
  }
  return selection;
}

// A fixed box fills the layout viewport, which on a phone grows past the screen when the page overflows sideways.
// Pin the sheet to the visual viewport instead so it always covers exactly what is on screen.
export function fitToViewport(element, viewport) {
  if (!viewport) return;
  Object.assign(element.style, {
    inset: 'auto',
    left: `${viewport.offsetLeft}px`,
    top: `${viewport.offsetTop}px`,
    width: `${viewport.width}px`,
    height: `${viewport.height}px`,
  });
}

function isVisible(element) {
  return element.getClientRects().length > 0;
}

function initializeFilter(page, environment) {
  const grid = page.querySelector('[data-bp-filter-grid]');
  const openButton = page.querySelector('[data-bp-filter-open]');
  const root = page.querySelector('[data-bp-filter-root]');
  const panel = root?.querySelector('[data-bp-filter-panel]');
  if (!grid || !openButton || !panel || page.hasAttribute(READY_ATTRIBUTE)) return false;

  const documentObject = environment.document ?? page.ownerDocument;
  const windowObject = environment.window ?? documentObject.defaultView;
  const historyObject = environment.history ?? windowObject.history;
  const locationObject = environment.location ?? windowObject.location;

  const items = Array.from(grid.querySelectorAll('.w-dyn-item'));
  if (items.length === 0) return false;
  const groupElements = Array.from(panel.querySelectorAll('[data-bp-filter-group]'));
  const groups = groupElements.map((element) => element.getAttribute('data-bp-filter-group'));
  const products = items.map((item) => {
    const facets = {};
    for (const field of item.querySelectorAll('[data-bp-facets] [data-f]')) {
      facets[field.getAttribute('data-f')] = parseFacetList(field.textContent);
    }
    return facets;
  });
  const options = collectOptions(products, groups);

  const inputs = [];
  for (const groupElement of groupElements) {
    const group = groupElement.getAttribute('data-bp-filter-group');
    const container = groupElement.querySelector('[data-bp-filter-options]');
    const toggle = groupElement.querySelector('[data-bp-filter-toggle]');
    if (options[group].length === 0 || !container) {
      groupElement.hidden = true;
      continue;
    }
    for (const value of options[group]) {
      const label = documentObject.createElement('label');
      label.setAttribute('data-bp-filter-option', '');
      const input = documentObject.createElement('input');
      input.type = 'checkbox';
      input.name = group;
      input.value = value;
      const text = documentObject.createElement('span');
      text.textContent = value;
      label.append(input, text);
      container.append(label);
      inputs.push(input);
    }
    toggle?.addEventListener('click', () => {
      toggle.setAttribute('aria-expanded', String(toggle.getAttribute('aria-expanded') !== 'true'));
    });
  }

  const status = documentObject.createElement('p');
  status.setAttribute('data-bp-filter-status', '');
  status.setAttribute('role', 'status');
  openButton.parentElement.append(status);
  const empty = documentObject.createElement('p');
  empty.setAttribute('data-bp-filter-empty', '');
  empty.textContent = 'No products match these filters.';
  empty.hidden = true;
  grid.after(empty);
  const more = documentObject.createElement('button');
  more.type = 'button';
  more.setAttribute('data-bp-filter-more', '');
  more.textContent = 'Load More';
  more.hidden = true;
  empty.after(more);

  let applied = queryToSelection(locationObject.search, options);
  let limit = PAGE_SIZE;
  const phone = windowObject.matchMedia?.(PHONE_QUERY);

  const readInputs = () => {
    const selection = Object.fromEntries(groups.map((group) => [group, []]));
    for (const input of inputs) if (input.checked) selection[input.name].push(input.value);
    return selection;
  };
  const writeInputs = (selection) => {
    for (const input of inputs) input.checked = (selection[input.name] ?? []).includes(input.value);
  };

  const render = (announce) => {
    const matches = matchProducts(products, applied);
    const visible = phone?.matches ? limitMatches(matches, limit) : matches;
    let shown = 0;
    items.forEach((item, index) => {
      item.style.display = visible[index] ? '' : 'none';
      if (visible[index]) shown += 1;
    });
    empty.hidden = shown > 0;
    more.hidden = shown === matches.filter(Boolean).length;
    const count = Object.values(applied).reduce((sum, values) => sum + values.length, 0);
    openButton.setAttribute('data-bp-filter-count', String(count));
    if (announce) status.textContent = `Showing ${shown} of ${items.length} products`;
  };

  const commit = () => {
    applied = readInputs();
    limit = PAGE_SIZE;
    render(true);
    const query = selectionToQuery(applied, groups, locationObject.search);
    historyObject.replaceState(historyObject.state, '', `${locationObject.pathname}${query}${locationObject.hash}`);
  };

  const focusables = () => Array.from(panel.querySelectorAll(FOCUSABLE)).filter(isVisible);
  const isOpen = () => root.getAttribute('data-state') === OPEN_STATE;
  const viewport = windowObject.visualViewport;
  const fit = () => fitToViewport(root, viewport);

  const open = () => {
    writeInputs(applied);
    for (const groupElement of groupElements) {
      const toggle = groupElement.querySelector('[data-bp-filter-toggle]');
      if (groupElement.querySelector('input:checked')) toggle?.setAttribute('aria-expanded', 'true');
    }
    fit();
    viewport?.addEventListener('resize', fit);
    viewport?.addEventListener('scroll', fit);
    root.setAttribute('data-state', OPEN_STATE);
    openButton.setAttribute('aria-expanded', 'true');
    focusables()[0]?.focus({ preventScroll: true });
  };
  const close = () => {
    if (!isOpen()) return;
    viewport?.removeEventListener('resize', fit);
    viewport?.removeEventListener('scroll', fit);
    writeInputs(applied);
    root.removeAttribute('data-state');
    openButton.setAttribute('aria-expanded', 'false');
    openButton.focus();
  };

  more.addEventListener('click', () => {
    const before = items.filter((item) => item.style.display !== 'none');
    limit += PAGE_SIZE;
    render(true);
    const next = items.find((item) => item.style.display !== 'none' && !before.includes(item));
    if (!next) return;
    next.setAttribute('tabindex', '-1');
    next.focus();
  });
  phone?.addEventListener?.('change', () => render(false));

  openButton.addEventListener('click', open);
  panel.querySelector('[data-bp-filter-close]')?.addEventListener('click', close);
  root.querySelector('[data-bp-filter-backdrop]')?.addEventListener('click', close);
  panel.querySelector('[data-bp-filter-apply]')?.addEventListener('click', () => {
    commit();
    close();
  });
  panel.querySelector('[data-bp-filter-clear]')?.addEventListener('click', () => {
    writeInputs({});
    commit();
  });

  documentObject.addEventListener('keydown', (event) => {
    if (!isOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const list = focusables();
    if (list.length === 0) return;
    const first = list[0];
    const last = list[list.length - 1];
    const active = documentObject.activeElement;
    if (!panel.contains(active)) {
      event.preventDefault();
      first.focus();
    } else if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  });

  openButton.setAttribute('aria-expanded', 'false');
  writeInputs(applied);
  render(false);
  page.setAttribute(READY_ATTRIBUTE, '');
  return true;
}

export function initializeProductFilter(root, environment = {}) {
  let initialized = 0;
  for (const page of findAll(root, CAPABILITY_SELECTOR)) {
    if (initializeFilter(page, environment)) initialized += 1;
  }
  return initialized;
}
